import { z } from 'zod';
import { indianMobileSchema, isoDateStringSchema, objectIdSchema } from './common.js';
import {
  notificationEventSchema,
  notificationSubjectSchema,
  notificationVariableKeySchema,
  NOTIFICATION_EVENTS,
} from '../notifications/events.js';

/**
 * The wire contract for the messaging layer: how a shop connects WhatsApp, how each event is
 * mapped to one of that shop's own templates, and what the message log shows.
 *
 * The vocabulary here is deliberately provider-neutral — `templateName`, `variables`,
 * `sendingNumber`, `providerMessageId`. Task 14's rule is that no field name belonging to the
 * messaging provider appears outside its client implementation, and this file is the contract
 * both the API and the browser validate against, so it is the first place that rule would break.
 *
 * The API key is the shape's whole security story: it goes UP in `apiKey` and never comes back
 * down. Nothing in any response schema below can carry it — the connection view has room for
 * the last four characters and nothing more.
 */

// --- Connecting the workspace ----------------------------------------------------------------

export const CONNECTION_STATUSES = ['not_connected', 'connected', 'error'] as const;
export type MessagingConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const messagingConnectionSchema = z.object({
  status: z.enum(CONNECTION_STATUSES),
  /** The workspace the key belongs to, as the provider reported it at verification. */
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable(),
  /** The number customers will see the message come from. */
  sendingNumber: z.string().nullable(),
  /** Enough to recognise which key is saved, and useless to anyone who reads it. */
  apiKeyLast4: z.string().nullable(),
  verifiedAt: isoDateStringSchema.nullable(),
  /** The provider's own words when the last verification failed. Never a generic sentence. */
  lastError: z.string().nullable(),
  /** False when the process is running against the in-development fake. */
  live: z.boolean(),
});
export type MessagingConnection = z.infer<typeof messagingConnectionSchema>;

export const updateMessagingConnectionSchema = z
  .object({
    /** Write-only. Omit to keep the stored key and change only the other fields. */
    apiKey: z.string().trim().min(8, 'That API key looks too short').max(400).optional(),
    workspaceId: z.string().trim().min(1).max(120).nullable().optional(),
    sendingNumber: indianMobileSchema.nullable().optional(),
  })
  .strict();
export type UpdateMessagingConnectionInput = z.infer<typeof updateMessagingConnectionSchema>;

// --- Quiet hours and rate limits ---------------------------------------------------------------

const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour HH:MM time');

/**
 * A shop pays its provider per conversation and a customer remembers being messaged at 6am, so
 * quiet hours are on by default. The window may wrap midnight — 21:00 to 08:00 is the normal
 * setting, not the edge case.
 *
 * The offset is stored rather than an IANA zone name: the whole market is one timezone, and a
 * number cannot be wrong in the way a server with no ICU data reading `Asia/Kolkata` can.
 */
export const quietHoursSchema = z
  .object({
    enabled: z.boolean(),
    start: timeOfDaySchema,
    end: timeOfDaySchema,
    utcOffsetMinutes: z.number().int().min(-720).max(840),
  })
  .refine((value) => !value.enabled || value.start !== value.end, {
    message: 'Quiet hours cannot start and end at the same minute',
    path: ['end'],
  });
export type QuietHours = z.infer<typeof quietHoursSchema>;

export const DEFAULT_QUIET_HOURS: QuietHours = Object.freeze({
  enabled: true,
  start: '21:00',
  end: '08:00',
  // IST. See the note above on why this is a number.
  utcOffsetMinutes: 330,
});

export const MAX_MESSAGES_PER_RECIPIENT_PER_DAY = 20;
export const DEFAULT_MESSAGES_PER_RECIPIENT_PER_DAY = 4;

export const messagingLimitsSchema = z.object({
  /** Counted over a rolling 24 hours. Beyond it a message waits, and is never dropped. */
  maxPerRecipientPerDay: z.number().int().min(1).max(MAX_MESSAGES_PER_RECIPIENT_PER_DAY),
});
export type MessagingLimits = z.infer<typeof messagingLimitsSchema>;

// --- Event → template mapping -------------------------------------------------------------------

/**
 * One placeholder of a template, bound to a piece of shop data or to a fixed string. Both are
 * needed: `{{1}}` is usually the customer's name, but a shop that ends every message with its
 * own tagline binds that as a literal rather than inventing a variable for it.
 */
/**
 * Note the absence of `.default(null)` on either half. These schemas validate RESPONSES as well
 * as requests — the browser parses what the API sends with the very same object — and a Zod
 * default makes the input type optional while the output type is not, which is a shape the two
 * sides can no longer agree on. Both keys are therefore always present on the wire.
 */
export const variableBindingSchema = z
  .object({
    position: z.number().int().min(1).max(30),
    variable: notificationVariableKeySchema.nullable(),
    literal: z.string().trim().max(200).nullable(),
  })
  .strict()
  .refine((value) => value.variable === null || value.literal === null, {
    message: 'Bind a placeholder to a field or to fixed text, not to both',
    path: ['literal'],
  });
export type VariableBinding = z.infer<typeof variableBindingSchema>;

export const eventMappingSchema = z.object({
  event: notificationEventSchema,
  label: z.string(),
  description: z.string(),
  audience: z.enum(['customer', 'owner']),
  /** Independently switchable, per the brief: a shop may want "ready" and nothing else. */
  enabled: z.boolean(),
  templateName: z.string().nullable(),
  templateLanguage: z.string().nullable(),
  bindings: z.array(variableBindingSchema),
  /** Variables this event may bind, so the UI never offers a field the API would refuse. */
  availableVariables: z.array(notificationVariableKeySchema),
  /** False when the event is switched on but has no template chosen yet — nothing will send. */
  ready: z.boolean(),
});
export type EventMapping = z.infer<typeof eventMappingSchema>;

export const updateEventMappingSchema = z
  .object({
    enabled: z.boolean().optional(),
    templateName: z.string().trim().min(1).max(200).nullable().optional(),
    templateLanguage: z.string().trim().min(2).max(20).nullable().optional(),
    bindings: z.array(variableBindingSchema).max(30).optional(),
  })
  .strict();
export type UpdateEventMappingInput = z.infer<typeof updateEventMappingSchema>;

// --- Reminders and rework (task 15) ---------------------------------------------------------------

/**
 * The three decisions that turn "we can send messages" into "we send the right ones".
 *
 * All three are per shop because all three are judgement calls a shop makes about its own
 * customers, and getting any of them wrong costs money and goodwill in the same message.
 */
export const reminderSettingsSchema = z
  .object({
    /**
     * How long before the promised date a "your order is due" nudge goes out. Zero switches the
     * reminder off entirely, which is the same thing the owner achieves by disabling the event
     * — two doors to one room, because owners look for it in both places.
     */
    deliveryReminderLeadHours: z
      .number()
      .int()
      .min(0)
      .max(24 * 7),
    /** Days after hand-over before an unpaid balance is chased. Zero switches it off. */
    balanceReminderAfterDays: z.number().int().min(0).max(90),
    /**
     * Whether an order sent back for rework may announce itself ready a SECOND time.
     *
     * Off by default, and that default is the acceptance criterion: a garment that fails
     * inspection, goes back to stitching and returns to ready must not tell the customer twice
     * that it is done. A shop whose rework cycles are long enough to be worth re-announcing can
     * turn this on and accept the extra conversation.
     */
    resendOnRework: z.boolean(),
  })
  .strict();
export type ReminderSettings = z.infer<typeof reminderSettingsSchema>;

export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = Object.freeze({
  deliveryReminderLeadHours: 24,
  balanceReminderAfterDays: 3,
  resendOnRework: false,
});

export const updateMessagingSettingsSchema = z
  .object({
    quietHours: quietHoursSchema.optional(),
    limits: messagingLimitsSchema.optional(),
    reminders: reminderSettingsSchema.optional(),
  })
  .strict();
export type UpdateMessagingSettingsInput = z.infer<typeof updateMessagingSettingsSchema>;

export const messagingSettingsSchema = z.object({
  connection: messagingConnectionSchema,
  quietHours: quietHoursSchema,
  limits: messagingLimitsSchema,
  reminders: reminderSettingsSchema,
  /** Every event in the catalogue, mapped or not — the screen is the catalogue. */
  events: z.array(eventMappingSchema).length(NOTIFICATION_EVENTS.length),
});
export type MessagingSettings = z.infer<typeof messagingSettingsSchema>;

// --- The shop's templates, as the provider reports them ------------------------------------------

export const messageTemplateSchema = z.object({
  name: z.string(),
  language: z.string(),
  /** `approved` templates are the only ones that can actually send. */
  status: z.enum(['approved', 'pending', 'rejected', 'unknown']),
  category: z.string().nullable(),
  body: z.string(),
  /** Placeholder numbers found in the body, so the mapping form can list exactly those rows. */
  placeholders: z.array(z.number().int().positive()),
});
export type MessageTemplate = z.infer<typeof messageTemplateSchema>;

export const messageTemplateListSchema = z.object({
  templates: z.array(messageTemplateSchema),
  /** Present when the workspace could not be reached; the list is then the last known one. */
  error: z.string().nullable(),
});
export type MessageTemplateList = z.infer<typeof messageTemplateListSchema>;

// --- Preview and test send ------------------------------------------------------------------------

export const messagePreviewSchema = z.object({
  event: notificationEventSchema,
  templateName: z.string().nullable(),
  /** The body with sample data substituted — what a customer would actually read. */
  rendered: z.string(),
  /** Placeholder → resolved sample value, so an unbound `{{3}}` is visible as such. */
  values: z.record(z.string()),
  /** Reasons this mapping would not send today: no template, unbound placeholder, not approved. */
  problems: z.array(z.string()),
});
export type MessagePreview = z.infer<typeof messagePreviewSchema>;

export const testSendSchema = z
  .object({
    event: notificationEventSchema,
    /** The owner's own number. A test send never goes to a customer. */
    to: indianMobileSchema,
  })
  .strict();
export type TestSendInput = z.infer<typeof testSendSchema>;

// --- The message log ---------------------------------------------------------------------------

/**
 * The life of one message.
 *
 *  `queued`     accepted by the CRM, waiting for the next dispatch pass
 *  `scheduled`  deferred — quiet hours or this customer's daily cap; `nextAttemptAt` says when
 *  `sent`       the provider accepted it
 *  `delivered`  it reached the handset            (from a delivery receipt)
 *  `read`       the customer opened it            (from a delivery receipt)
 *  `failed`     the last attempt failed and another is due
 *  `dead_letter` retries exhausted; a person has to look at it
 */
export const MESSAGE_STATUSES = [
  'queued',
  'scheduled',
  'sent',
  'delivered',
  'read',
  'failed',
  'dead_letter',
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);

/** Why a message is waiting rather than gone. */
export const DEFERRAL_REASONS = ['quiet_hours', 'rate_limit', 'retry'] as const;
export type DeferralReason = (typeof DEFERRAL_REASONS)[number];

export const messageLogEntrySchema = z.object({
  id: objectIdSchema,
  event: notificationEventSchema,
  status: messageStatusSchema,
  /** Who it went to, as dialled. */
  to: z.string(),
  recipientName: z.string().nullable(),
  templateName: z.string().nullable(),
  /** The values as they were actually resolved at send time — the log is the evidence. */
  variables: z.record(z.string()),
  renderedBody: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  deferredReason: z.enum(DEFERRAL_REASONS).nullable(),
  nextAttemptAt: isoDateStringSchema.nullable(),
  error: z.string().nullable(),
  /** What the message is about, so the log can link back to the order. */
  subjectType: notificationSubjectSchema.nullable(),
  subjectId: z.string().nullable(),
  subjectLabel: z.string().nullable(),
  test: z.boolean(),
  /** True when a person pressed send on the order screen, rather than a domain event firing. */
  manual: z.boolean(),
  createdAt: isoDateStringSchema,
  sentAt: isoDateStringSchema.nullable(),
  deliveredAt: isoDateStringSchema.nullable(),
  readAt: isoDateStringSchema.nullable(),
});
export type MessageLogEntry = z.infer<typeof messageLogEntrySchema>;

export const messageLogQuerySchema = z.object({
  event: notificationEventSchema.optional(),
  status: messageStatusSchema.optional(),
  /** Recipient number or order number. */
  q: z.string().trim().max(60).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type MessageLogQuery = z.infer<typeof messageLogQuerySchema>;

export const messageLogPageSchema = z.object({
  items: z.array(messageLogEntrySchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  /** Messages sitting in `dead_letter`, so the screen can shout about them from page one. */
  deadLetterCount: z.number().int().nonnegative(),
});
export type MessageLogPage = z.infer<typeof messageLogPageSchema>;

// --- One order's messages (task 15) -----------------------------------------------------------

/**
 * The order screen's messaging surface: what this customer has already been told about this
 * order, and what staff may tell them now.
 *
 * It exists because "did she get the message?" and "tell her it's ready" are both counter
 * questions, asked with the customer on the phone — and neither is answerable from a settings
 * screen listing every message the shop has ever sent.
 */
export const orderMessageOptionSchema = z.object({
  event: notificationEventSchema,
  label: z.string(),
  description: z.string(),
  /** Whether this event fires by itself. A manual send may use it either way. */
  enabled: z.boolean(),
  /** False when nothing would send automatically — no template chosen, or none approved. */
  ready: z.boolean(),
  /** Already sent for this order, so the screen can say so instead of offering it blindly. */
  alreadySent: z.boolean(),
});
export type OrderMessageOption = z.infer<typeof orderMessageOptionSchema>;

export const orderMessagesSchema = z.object({
  orderId: objectIdSchema,
  /**
   * False when this shop has not connected a WhatsApp workspace. The screen then offers the
   * one-tap `wa.me` fallback instead of the send button — and says what connecting would buy.
   */
  relayConnected: z.boolean(),
  /** Every message about this order, newest first, with its delivery status. */
  messages: z.array(messageLogEntrySchema),
  /** What staff may send from here. */
  options: z.array(orderMessageOptionSchema),
});
export type OrderMessages = z.infer<typeof orderMessagesSchema>;

/** The same card, about an alteration. */
export const alterationMessagesSchema = z.object({
  alterationId: objectIdSchema,
  relayConnected: z.boolean(),
  messages: z.array(messageLogEntrySchema),
  options: z.array(orderMessageOptionSchema),
});
export type AlterationMessages = z.infer<typeof alterationMessagesSchema>;

export const sendOrderMessageSchema = z
  .object({
    event: notificationEventSchema,
  })
  .strict();
export type SendOrderMessageInput = z.infer<typeof sendOrderMessageSchema>;

/**
 * What this message would say about THIS order — real values, not the settings screen's samples.
 *
 * Preview before send is the acceptance criterion, and the reason is that a manual send is the
 * one path with no automatic gate in front of it: nobody has approved the wording for this
 * customer, so the person pressing the button has to be able to read it first.
 */
export const orderMessagePreviewSchema = z.object({
  event: notificationEventSchema,
  templateName: z.string().nullable(),
  /** The mapped template with this order's values in it. Empty when there is no template. */
  rendered: z.string(),
  values: z.record(z.string()),
  problems: z.array(z.string()),
  /** Where it would go, as dialled. */
  to: z.string(),
  /** True when Relay could send this without a person: connected, mapped and approved. */
  canSendAutomatically: z.boolean(),
  /** The message as the shop would type it themselves — always present, template or not. */
  fallbackText: z.string(),
  /** One tap: opens WhatsApp on the staff member's own phone with `fallbackText` written. */
  fallbackUrl: z.string(),
});
export type OrderMessagePreview = z.infer<typeof orderMessagePreviewSchema>;
